<script setup lang="ts">
import DialogCloseIcon from "@/assets/editViewIcons/DialogCloseIcon.vue";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {useDialogPluginComponent} from "quasar";
import {GridApi} from "ag-grid-community";
import Translator from "@/utils/common/Translator.ts";
import {onBeforeMount, ref} from "vue";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";

const {dialogRef, onDialogCancel} = useDialogPluginComponent()
const props = defineProps<{ gridApi: GridApi }>();
const api = props.gridApi
const sortedColumnIds = ref<string[]>([])

function fetchSortedColumnIds() {
  const columnState = api.getColumnState();
  const sortedColumns = columnState.filter(col => col.sort)
    .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0));
  sortedColumnIds.value = sortedColumns.map(s => s.colId)
}

async function removeSortFromColumn(colId: string) {
  const currentState = api.getColumnState();
  const updatedState = currentState.map(col => {
    if (col.colId === colId)
      return {...col, sort: null, sortIndex: null};
    else
      return col;
  });
  const deleted = api.applyColumnState({state: updatedState});
  if (!deleted)
    return;
  sortedColumnIds.value = sortedColumnIds.value.filter(id => id !== colId)
}

async function resetAllColumnSorts() {
  const currentState = api.getColumnState();
  const clearedState = currentState.map(col => ({
    ...col,
    sort: null,
    sortIndex: null
  }));
  const deleted = api.applyColumnState({state: clearedState});
  if (!deleted)
    return;
  sortedColumnIds.value = []
}

onBeforeMount(fetchSortedColumnIds)
</script>

<template>
  <q-dialog ref="dialogRef" transition-hide="jump-down" transition-show="jump-up">
    <q-card class="tw-flex tw-flex-col tw-justify-between tw-relative tw-w-[450px] dark:tw-bg-namaBackground-deep-dark tw-px-4 tw-py-3 !tw-rounded-xl"
            dir="ltr">
      <div>
        <div class="tw-flex tw-justify-between tw-items-center">
          <div class="tw-flex tw-items-center tw-gap-2">
            <NamaIcon default-icon="nm-sort" class="!tw-text-[35px]"/>
            <p v-text="Translator.translateId('sortColumns')" class="tw-text-lg tw-font-semibold tw-leading-none dark:tw-text-white"/>
          </div>
          <q-avatar square class="tw-size-5  tw-cursor-pointer" @click="onDialogCancel">
            <DialogCloseIcon/>
          </q-avatar>
        </div>
        <q-separator class="tw-my-2 dark:tw-bg-white/10"/>
      </div>
      <div class="tw-p-2 tw-w-full tw-flex tw-flex-col tw-gap-2 tw-items-center tw-overflow-y-auto tw-h-40">
        <ul class="tw-w-full tw-flex tw-flex-col tw-relative tw-px-5 tw-py-2">
          <template v-for="(colId,index) in sortedColumnIds">
            <li class="tw-flex tw-items-center tw-justify-between">
              <span v-text="Translator.translateAGColumn(gridApi,colId)" class="dark:tw-text-white/70"/>
              <q-btn icon="close" size="sm" round :color="useNamaFlagsStore().darkMode?'grey-9':'grey-5'"
                     @click="removeSortFromColumn(colId)"/>
            </li>
            <q-separator class="tw-my-2 dark:tw-bg-white/10" v-if="index !== sortedColumnIds.length -1 "/>
          </template>
        </ul>
        <q-separator class="tw-my-2 dark:tw-bg-white/10"/>
      </div>
      <q-btn class="tw-w-full" color="red-4" @click="resetAllColumnSorts" :disable="!sortedColumnIds">
        <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
            <span v-text="Translator.translateId('reset')"
                  class="tw-capitalize tw-font-semibold"/>
          <NamaIcon default-icon="nm-sort-close" class="active-white"/>
        </div>
      </q-btn>
    </q-card>
  </q-dialog>
</template>
